// This file contains the G&S mapping config, categorized by different sections
// Each object contains the form field name, the item's subcategory, and the prop that matches the field.
// File does not contain checkbox related mapping
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'

const Pb = PbSubcategory

export type sectionMapConfig = {
	name: string
	category: PbProductType | PbSubcategory | string
	prop: string
}[]

export enum PriceTotalKey {
	ServicesTotal = 'servicesTotal',
	MerchandiseTotal = 'merchandiseTotal',
	GoodsAndServicesTotal = 'goodsAndServicesTotal',
	CashAdvanceTotal = 'cashAdvanceTotal',
	FuneralSubtotal = 'funeralSubtotal',
	FuneralTotal = 'funeralTotal',
}
// Fields for section A - Guaranteed Services
export const gServiceMapSubItemsConfig: sectionMapConfig = [
	{
		name: 'gServiceTypeOtherDesc',
		category: `${PbProductType.Disposition}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'gBasicServicesDesc',
		category: Pb.ServiceBasicServicesOfFDAndStaff,
		prop: 'shortDescription',
	},
	{
		name: 'gBasicServicesAmt',
		category: Pb.ServiceBasicServicesOfFDAndStaff,
		prop: 'price',
	},
	{
		name: 'gRefrigerationDaysVal',
		category: Pb.ServiceRefrigeration,
		prop: 'numberOfDays',
	},
	{
		name: 'gEmbalmingRefrigerationAmt',
		category: Pb.ServiceEmbalming,
		prop: 'price',
	},
	{
		name: 'gOtherPrepDesc',
		category: Pb.ServiceBodyPreparation,
		prop: 'shortDescription',
	},
	{
		name: 'gOtherPrepAmt',
		category: Pb.ServiceBodyPreparation,
		prop: 'price',
	},
	{
		name: 'gVisitationVal',
		category: Pb.ServiceVisitationViewing,
		prop: 'numberOfDays',
	},
	{
		name: 'gVisitationPerDayAmt',
		category: Pb.ServiceVisitationViewing,
		prop: 'amountPerDay',
	},
	{
		name: 'gVisitationAmt',
		category: Pb.ServiceVisitationViewing,
		prop: 'price',
	},
	{
		name: 'gFuneralMemorialServiceDesc',
		category: Pb.ServiceFuneralMemorial,
		prop: 'shortDescription',
	},
	{
		name: 'gFuneralMemorialServiceAmt',
		category: Pb.ServiceFuneralMemorial,
		prop: 'price',
	},
	{
		name: 'gGravesideServiceDesc',
		category: Pb.ServiceGravesideService,
		prop: 'shortDescription',
	},
	{
		name: 'gGravesideServiceAmt',
		category: Pb.ServiceGravesideService,
		prop: 'price',
	},
	// as both section B and C Cremation has the same category, guaranteed cremation has a custom key
	{
		name: 'gCremationServiceDesc',
		category: `${Pb.ServiceCremationService}`,
		prop: 'shortDescription',
	},
	{
		name: 'gCremationServiceAmt',
		category: `${Pb.ServiceCremationService}`,
		prop: 'price',
	},
	{
		name: 'gForwardingReceivingRemainsDesc',
		category: Pb.ServiceForwardingAndReceivingRemains,
		prop: 'shortDescription',
	},
	{
		name: 'gForwardingReceivingRemainsAmt',
		category: Pb.ServiceForwardingAndReceivingRemains,
		prop: 'price',
	},
	{
		name: 'gTransferMileRadiusVal',
		category: Pb.TransportationTransfer,
		prop: 'mileRadiusForStandardService',
	},
	{
		name: 'gTransferMileRadiusAmt',
		category: Pb.TransportationTransfer,
		prop: 'price',
	},
	{
		name: 'gHearseMileRadiusVal',
		category: Pb.TransportationHearse,
		prop: 'mileRadiusForStandardService',
	},
	{
		name: 'gHearseMileRadiusAmt',
		category: Pb.TransportationHearse,
		prop: 'price',
	},
	{
		name: 'gHearseAdditionalMilesVal',
		category: Pb.TransportationHearse,
		prop: 'specificMileAllotment',
	},
	{
		name: 'gHearsePerMileAmt',
		category: Pb.TransportationHearse,
		prop: 'amountPerMile',
	},
	{
		name: 'gFamilyCarLimoMileRadiusVal',
		category: Pb.TransportationFamilyCarLimo,
		prop: 'mileRadiusForStandardService',
	},
	{
		name: 'gFamilyCarLimoMileRadiusAmt',
		category: Pb.TransportationFamilyCarLimo,
		prop: 'price',
	},
	{
		name: 'gFamilyCarAdditionalMilesVal',
		category: Pb.TransportationFamilyCarLimo,
		prop: 'specificMileAllotment',
	},
	{
		name: 'gFamilyCarLimoPerMileAmt',
		category: Pb.TransportationFamilyCarLimo,
		prop: 'amountPerMile',
	},
	{
		name: 'gUtilityVehicleVanDesc',
		category: Pb.TransportationUtilityVehicleVan,
		prop: 'shortDescription',
	},
	{
		name: 'gUtilityVehicleVanAmt',
		category: Pb.TransportationUtilityVehicleVan,
		prop: 'price',
	},
	// The following two (gOtherServicesLine1Desc gOtherServicesLine1Amt) definitions
	// should be removed once all pdfs have renamed services other lines to transportation other lines.
	{
		name: 'gOtherServicesLine1Desc',
		category: `${PbProductType.Transportation}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'gOtherServicesLine1Amt',
		category: `${PbProductType.Transportation}-${Pb.Other}`,
		prop: 'price',
	},
	{
		name: 'gOtherTransportationLine1Desc',
		category: `${PbProductType.Transportation}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'gOtherTransportationLine1Amt',
		category: `${PbProductType.Transportation}-${Pb.Other}`,
		prop: 'price',
	},
	{
		name: 'gOtherServicesLine2Desc',
		category: `${PbProductType.Transportation}-${Pb.Other2}`,
		prop: 'name',
	},
	{
		name: 'gOtherServicesLine2Amt',
		category: `${PbProductType.Transportation}-${Pb.Other2}`,
		prop: 'price',
	},
	{
		name: 'gOtherTransportationLine2Desc',
		category: `${PbProductType.Transportation}-${Pb.Other2}`,
		prop: 'name',
	},
	{
		name: 'gOtherTransportationLine2Amt',
		category: `${PbProductType.Transportation}-${Pb.Other2}`,
		prop: 'price',
	},
	{
		name: 'packageName',
		category: `${PbProductType.Package}`,
		prop: 'name',
	},
]

// Fields for section B - Guaranteed Merchandise
export const gMerchandiseMapConfig: sectionMapConfig = [
	{
		name: 'gCasketManufacturerDesc',
		category: Pb.MerchandiseCasket,
		prop: 'productManufacturer',
	},
	{
		name: 'gCasketModelDesc',
		category: Pb.MerchandiseCasket,
		prop: 'modelNameNumber',
	},
	{
		name: 'gCasketInteriorDesc',
		category: Pb.MerchandiseCasket,
		prop: 'interiorDescription',
	},
	{
		name: 'gCasketExteriorDesc',
		category: Pb.MerchandiseCasket,
		prop: 'exteriorDescription',
	},
	{ name: 'gCasketAmt', category: Pb.MerchandiseCasket, prop: 'price' },
	{
		name: 'gOuterBurialContainerManufacturerDesc',
		category: Pb.MerchandiseOuterBurialContainer,
		prop: 'productManufacturer',
	},
	{
		name: 'gOuterBurialModelDesc',
		category: Pb.MerchandiseOuterBurialContainer,
		prop: 'modelNameNumber',
	},
	{
		name: 'gOuterBurialConstructedOfDesc',
		category: Pb.MerchandiseOuterBurialContainer,
		prop: 'exteriorDescription',
	},
	{
		name: 'gOuterBurialAmt',
		category: Pb.MerchandiseOuterBurialContainer,
		prop: 'price',
	},

	{
		name: 'gUrnManufacturerDesc',
		category: Pb.MerchandiseUrn,
		prop: 'productManufacturer',
	},
	{
		name: 'gUrnModelDesc',
		category: Pb.MerchandiseUrn,
		prop: 'modelNameNumber',
	},
	{
		name: 'gUrnConstructedOfDesc',
		category: Pb.MerchandiseUrn,
		prop: 'exteriorDescription',
	},
	{ name: 'gUrnAmt', category: Pb.MerchandiseUrn, prop: 'price' },
	// as both section B and C Cremation has the same category, guaranteed RegisterBook has a custom key
	{
		name: 'gBookFoldersCardsAmt',
		category: Pb.MerchandiseRegisterBookMemorialFoldersAckCards,
		prop: 'price',
	},
	{
		name: 'gOtherMerchandiseLine1Desc',
		category: `${PbProductType.Merchandise}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'gOtherMerchandiseLine1Amt',
		category: `${PbProductType.Merchandise}-${Pb.Other}`,
		prop: 'price',
	},
	{
		name: 'gOtherMerchandiseLine2Desc',
		category: `${PbProductType.Merchandise}-${Pb.Other2}`,
		prop: 'shortDescription',
	},
	{
		name: 'gOtherMerchandiseLine2Amt',
		category: `${PbProductType.Merchandise}-${Pb.Other2}`,
		prop: 'price',
	},
]

// Fields for section C - Non-Guaranteed CashAdvance Items
export const ngCashAdvanceMapConfig: sectionMapConfig = [
	{
		name: 'ngDeathCertificatesQuantityVal',
		category: Pb.CashAdvanceDeathCertificates,
		prop: 'quantity',
	},
	{
		name: 'ngDeathCertificatesEachAmt',
		category: Pb.CashAdvanceDeathCertificates,
		prop: 'amountPerDeathCertificate',
	},
	{
		name: 'ngDeathCertificatesAmt',
		category: Pb.CashAdvanceDeathCertificates,
		prop: 'price',
	},
	{
		name: 'ngObituaryNoticePublicationLine1Desc',
		category: Pb.CashAdvanceObituary,
		prop: 'shortDescription',
	},
	{
		name: 'ngObituaryNoticePublicationAmt',
		category: Pb.CashAdvanceObituary,
		prop: 'price',
	},

	{
		name: 'ngFlowersDesc',
		category: Pb.CashAdvanceFlowers,
		prop: 'shortDescription',
	},
	{
		name: 'ngFlowersTypeDesc',
		category: Pb.CashAdvanceFlowers,
		prop: 'flowersType',
	},
	{ name: 'ngFlowersAmt', category: Pb.CashAdvanceFlowers, prop: 'price' },

	{
		name: 'ngBookFoldersCardsDesc',
		category: Pb.MerchandiseRegisterBookMemorialFoldersAckCards,
		prop: 'shortDescription',
	},
	{
		name: 'ngBookFoldersCardsAmt',
		category: Pb.MerchandiseRegisterBookMemorialFoldersAckCards,
		prop: 'price',
	},

	{
		name: 'ngHairdresserDesc',
		category: Pb.CashAdvanceHairdresser,
		prop: 'shortDescription',
	},
	{
		name: 'ngHairdresserAmt',
		category: Pb.CashAdvanceHairdresser,
		prop: 'price',
	},

	{
		name: 'ngClothingDesc',
		category: Pb.CashAdvanceClothing,
		prop: 'shortDescription',
	},
	{ name: 'ngClothingAmt', category: Pb.CashAdvanceClothing, prop: 'price' },

	{
		name: 'ngClergyHonorariumDesc',
		category: Pb.CashAdvanceClergyHonorarium,
		prop: 'shortDescription',
	},
	{
		name: 'ngClergyHonorariumAmt',
		category: Pb.CashAdvanceClergyHonorarium,
		prop: 'price',
	},

	{
		name: 'ngMusicianVocalistDesc',
		category: Pb.CashAdvanceMusicianVocalist,
		prop: 'shortDescription',
	},
	{
		name: 'ngMusicianVocalistAmt',
		category: Pb.CashAdvanceMusicianVocalist,
		prop: 'price',
	},

	{
		name: 'ngMusicDesc',
		category: Pb.CashAdvanceMusic,
		prop: 'shortDescription',
	},
	{ name: 'ngMusicAmt', category: Pb.CashAdvanceMusic, prop: 'price' },

	{
		name: 'ngCremationServicesDesc',
		category: Pb.CashAdvanceCremationServices,
		prop: 'shortDescription',
	},
	{
		name: 'ngCremationServicesAmt',
		category: Pb.CashAdvanceCremationServices,
		prop: 'price',
	},
	{
		name: 'ngOtherCashAdvanceLine1Desc',
		category: `${PbProductType.CashAdvance}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherCashAdvanceLine1Amt',
		category: `${PbProductType.CashAdvance}-${Pb.Other}`,
		prop: 'price',
	},
	{
		name: 'ngOtherCashAdvanceLine2Desc',
		category: `${PbProductType.CashAdvance}-${Pb.Other2}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherCashAdvanceLine2Amt',
		category: `${PbProductType.CashAdvance}-${Pb.Other2}`,
		prop: 'price',
	},
	// FamilyTravel
	{
		name: 'ngAirfareDesc',
		category: Pb.FamilyTravelAirfare,
		prop: 'shortDescription',
	},
	{ name: 'ngAirfareAmt', category: Pb.FamilyTravelAirfare, prop: 'price' },

	{
		name: 'ngLodgingDesc',
		category: Pb.FamilyTravelLodging,
		prop: 'shortDescription',
	},
	{ name: 'ngLodgingAmt', category: Pb.FamilyTravelLodging, prop: 'price' },
	// Cemetery
	{
		name: 'ngEscortNumberVal',
		category: Pb.CemeteryEscorts,
		prop: 'shortDescription',
	},
	{ name: 'ngEscortAmt', category: Pb.CemeteryEscorts, prop: 'price' },

	{
		name: 'ngIntermentWeekdayAmt',
		category: Pb.CemeteryInterment,
		prop: 'weekdayOpeningClosingCost',
	},
	{
		name: 'ngIntermentWeekendAmt',
		category: Pb.CemeteryInterment,
		prop: 'weekendHolidayOpeningClosingCost',
	},
	{ name: 'ngIntermentAmt', category: Pb.CemeteryInterment, prop: 'price' },

	{
		name: 'ngCemeterySpaceDesc',
		category: Pb.CemeterySpaces,
		prop: 'shortDescription',
	},
	// The following four fields all fill the same ngCemeterySpaceAmt field
	{
		name: 'ngCemeterySpaceAmt',
		category: Pb.CemeterySpaces,
		prop: 'price',
	},
	{
		name: 'ngCemeterySpaceAmt',
		category: Pb.CemeteryPlot,
		prop: 'price',
	},
	{
		name: 'ngCemeterySpaceAmt',
		category: Pb.CemeteryMausoleum,
		prop: 'price',
	},
	{
		name: 'ngCemeterySpaceAmt',
		category: Pb.CemeteryColumbarium,
		prop: 'price',
	},

	{
		name: 'ngVaultSettingDesc',
		category: Pb.CemeteryVaultSetting,
		prop: 'shortDescription',
	},
	{
		name: 'ngVaultSettingAmt',
		category: Pb.CemeteryVaultSetting,
		prop: 'price',
	},

	{
		name: 'ngMonumentMarkerAllowanceDesc',
		category: Pb.CemeteryMonumentMarker,
		prop: 'shortDescription',
	},
	{
		name: 'ngMonumentMarkerAllowanceAmt',
		category: Pb.CemeteryMonumentMarker,
		prop: 'price',
	},
	{
		name: 'ngOtherCemeteryDesc',
		category: `${PbProductType.Cemetery}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherCemeteryAmt',
		category: `${PbProductType.Cemetery}-${Pb.Other}`,
		prop: 'price',
	},
	// Afterward
	{
		name: 'ngAfterwardLocationDesc',
		category: Pb.AfterwardLocation,
		prop: 'shortDescription',
	},
	{
		name: 'ngAfterwardLocationAmt',
		category: Pb.AfterwardLocation,
		prop: 'price',
	},

	{
		name: 'ngAfterwardCateringDesc',
		category: Pb.AfterwardCatering,
		prop: 'shortDescription',
	},
	{
		name: 'ngAfterwardCateringAmt',
		category: Pb.AfterwardCatering,
		prop: 'price',
	},
	{
		name: 'ngOtherAfterwardDesc',
		category: `${PbProductType.Afterward}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherAfterwardAmt',
		category: `${PbProductType.Afterward}-${Pb.Other}`,
		prop: 'price',
	},
	// Other
	{
		name: 'ngFamilyAllowanceDesc',
		category: Pb.OtherFamilyAllowance,
		prop: 'shortDescription',
	},
	{
		name: 'ngFamilyAllowanceAmt',
		category: Pb.OtherFamilyAllowance,
		prop: 'price',
	},

	{
		name: 'ngSalesTaxPercentVal',
		category: Pb.OtherSalesTax,
		prop: 'shortDescription',
	},
	{ name: 'ngSalesTaxAmt', category: Pb.OtherSalesTax, prop: 'price' },

	{
		name: 'ngOtherOtherLine1Desc',
		category: `${PbProductType.Other}-${Pb.Other}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherOtherLine1Amt',
		category: `${PbProductType.Other}-${Pb.Other}`,
		prop: 'price',
	},
	{
		name: 'ngOtherOtherLine2Desc',
		category: `${PbProductType.Other}-${Pb.Other2}`,
		prop: 'shortDescription',
	},
	{
		name: 'ngOtherOtherLine2Amt',
		category: `${PbProductType.Other}-${Pb.Other2}`,
		prop: 'price',
	},
]

// Checkboxes that are checked when an item of their category is in the section
export type checkBoxMapConfig = {
	name: string
	category: PbProductType | PbSubcategory | string
}[]

export const gServiceCheckBoxMapConfig: checkBoxMapConfig = [
	{
		name: 'gServiceTypeFullServiceBurialCheck',
		category: Pb.DispositionFullServiceBurial,
	},
	{
		name: 'gServiceTypeFullServiceCremationCheck',
		category: Pb.DispositionFullServiceCremation,
	},
	{
		name: 'gServiceTypeDirectBurialCheck',
		category: Pb.DispositionDirectBurial,
	},
	{
		name: 'gServiceTypeDirectCremationCheck',
		category: Pb.DispositionDirectCremation,
	},
	{
		name: 'gServiceTypeMemorialServiceCremationCheck',
		category: Pb.DispositionMemorialServiceCremation,
	},
	{
		name: 'gServiceTypeOtherCheck',
		category: `${Pb.Other}-${PbProductType.Disposition}`,
	},
	{ name: 'gEmbalmingCheck', category: Pb.ServiceEmbalming },
	{ name: 'gRefrigerationCheck', category: Pb.ServiceRefrigeration },
]

// Only one cemetery type can be checked, so these are not checked by category alone
export const ngCemeteryCheckBoxMapConfig: checkBoxMapConfig = [
	{ name: 'ngCemeteryPlotCheck', category: Pb.CemeteryPlot },
	{ name: 'ngCemeteryMausoleumCheck', category: Pb.CemeteryMausoleum },
	{ name: 'ngCemeteryColumbariumCheck', category: Pb.CemeteryColumbarium },
]

export const priceTotalMapConfig: {
	name: string
	key: PriceTotalKey
}[] = [
	{
		name: 'gServicesTotal',
		key: PriceTotalKey.ServicesTotal,
	},
	{
		name: 'gMerchandiseTotal',
		key: PriceTotalKey.MerchandiseTotal,
	},
	{
		name: 'ngCashAdvanceItemsTotal',
		key: PriceTotalKey.CashAdvanceTotal,
	},
	{
		name: 'gGoodsAndServicesTotal',
		key: PriceTotalKey.GoodsAndServicesTotal,
	},
	{
		name: 'funeralAmountTotal',
		key: PriceTotalKey.FuneralTotal,
	},
]
